import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSEO, jsonLdBreadcrumb } from "@/lib/seo";
import { AlertCircle } from "lucide-react";

/* -------------------------------------------------------------------------- */
/*  Tab catalogue                                                              */
/* -------------------------------------------------------------------------- */
/*  Every tab is either:                                                       */
/*   - `source: "/legal/<file>.md"`  → fetched at runtime and rendered verbatim */
/*   - `component: <ReactComponent/>` → hand-authored (used for the four       */
/*     regulator-facing disclosures we intentionally kept from the previous    */
/*     Legal.jsx per the user's instruction).                                   */
/* -------------------------------------------------------------------------- */

const SECTIONS = [
  { id: "terms",       label: "Terms of Service",                          source: "/legal/terms-of-service.md" },
  { id: "privacy",     label: "Privacy Policy",                            source: "/legal/privacy-policy.md" },
  { id: "cookies",     label: "Cookie Policy",                             source: "/legal/cookie-policy.md" },
  { id: "marketplace", label: "Marketplace Agreement",                     source: "/legal/marketplace-agreement.md" },
  { id: "buyer",       label: "Buyer Agreement",                           source: "/legal/buyer-agreement.md" },
  { id: "seller",      label: "Seller Agreement",                          source: "/legal/seller-agreement.md" },
  { id: "closing",     label: "Transaction Management & Closing",          source: "/legal/transaction-management-agreement.md" },
  { id: "diligence",   label: "Due Diligence Policy",                      source: "/legal/due-diligence-policy.md" },
  { id: "portfolio",   label: "Portfolio Access Policy",                   source: "/legal/portfolio-access-policy.md" },
  { id: "fees",        label: "Fee Schedule",                              source: "/legal/fee-schedule.md" },
  { id: "risk",        label: "Risk Disclosure",                           source: "/legal/risk-disclosure.md" },
  { id: "licensing",   label: "State Licensing Disclosure",                Component: StateLicensing },
  { id: "tila",        label: "TILA Disclosure",                           Component: TILA },
  { id: "ecoa",        label: "ECOA Disclosure",                           Component: ECOA },
  { id: "scra",        label: "SCRA Disclosure",                           Component: SCRA },
];

const SEO_BY_TAB = {
  terms:       { title: "Terms of Service — FinanceLocal",                        desc: "The FinanceLocal Terms of Service. The binding agreement governing use of the peer-to-peer marketplace for vehicle-backed notes and related financial assets." },
  privacy:     { title: "Privacy Policy — FinanceLocal",                          desc: "How FinanceLocal collects, uses, shares, and safeguards user personal information on the marketplace." },
  cookies:     { title: "Cookie Policy — FinanceLocal",                           desc: "How FinanceLocal uses cookies and similar technologies to keep users signed in, secure the platform, and analyze site performance." },
  marketplace: { title: "Marketplace Agreement — FinanceLocal",                   desc: "Rules of participation for buyers, sellers, dealers, and investors on the FinanceLocal marketplace." },
  buyer:       { title: "Buyer Agreement — FinanceLocal",                         desc: "The agreement governing every purchase or attempted purchase of a financial asset made through FinanceLocal." },
  seller:      { title: "Seller Agreement — FinanceLocal",                        desc: "The agreement governing every listing, sale, assignment, origination, or transfer of a financial asset through FinanceLocal." },
  closing:     { title: "Transaction Management & Closing Agreement — FinanceLocal", desc: "How FinanceLocal coordinates buyer/seller closings, handles transaction funds, and disclaims escrow, trust, or fiduciary duty." },
  diligence:   { title: "Due Diligence Policy — FinanceLocal",                    desc: "Buyer responsibilities and platform recommendations for reviewing vehicle-backed notes, portfolios, and related financial assets before purchase." },
  portfolio:   { title: "Portfolio Access Policy — FinanceLocal",                 desc: "How buyers request access to confidential portfolio information (proof of funds, company details, seller approval) and the confidentiality obligations that follow." },
  fees:        { title: "Fee Schedule — FinanceLocal",                            desc: "The current FinanceLocal fee schedule: 2% buyer + 2% seller marketplace fees, $500 combined minimum, $199/30-day featured listings, origination fees, AI Transaction Assistant pricing." },
  risk:        { title: "Risk Disclosure — FinanceLocal",                         desc: "Investing in financial receivables through FinanceLocal involves risk of loss. Borrower default, collateral, documentation, liquidity, market, and AI risks are described in full." },
  licensing:   { title: "State Licensing Disclosure — FinanceLocal",              desc: "State licensing disclosure for dealers and finance parties operating on the FinanceLocal marketplace." },
  tila:        { title: "TILA Disclosure — FinanceLocal",                         desc: "Truth in Lending Act disclosure for consumer credit transactions originated through FinanceLocal." },
  ecoa:        { title: "ECOA Disclosure — FinanceLocal",                         desc: "Equal Credit Opportunity Act disclosure and manual credit review policy for FinanceLocal." },
  scra:        { title: "SCRA Disclosure — FinanceLocal",                         desc: "Servicemembers Civil Relief Act protections for active-duty borrowers on FinanceLocal." },
};

/* -------------------------------------------------------------------------- */
/*  Markdown-lite renderer                                                     */
/* -------------------------------------------------------------------------- */
/*  Purpose: render the source documents verbatim while preserving section    */
/*  headings, numbered sections, bullets, and separators — with zero external */
/*  dep. Supports only the small dialect used in FinanceLocal's legal docs:    */
/*    - `# H1`                                                                 */
/*    - Section headings that are a leading number followed by a period       */
/*      (e.g. `1. Eligibility`) become <h3>                                    */
/*    - Bullet lines starting with `•` or `- `                                 */
/*    - Horizontal rules made of `---`                                         */
/*    - Every other non-empty line = <p>                                       */
/* -------------------------------------------------------------------------- */

// Rule-of-thumb: don't inject any HTML from the source; render as plain text.
function LegalMarkdown({ text }) {
  const blocks = useMemo(() => parseMarkdown(text || ""), [text]);
  return (
    <div className="legal-doc" data-testid="legal-doc">
      {blocks.map((b, i) => renderBlock(b, i))}
    </div>
  );
}

function parseMarkdown(src) {
  const rawLines = src.replace(/\r\n/g, "\n").split("\n");
  // Drop the leading "N.      Title" tag line the source docs all start with,
  // and any leading whitespace-only lines. Keep the file itself untouched.
  let i = 0;
  while (i < rawLines.length && /^\s*\d+\.\s+[A-Z]/.test(rawLines[i]) === false && rawLines[i].trim() === "") i++;
  if (i < rawLines.length && /^\s*\d+\.\s+[A-Z][\w &]/.test(rawLines[i])) i++;
  const lines = rawLines.slice(i);

  const blocks = [];
  let bulletBuf = null;

  const flushBullets = () => {
    if (bulletBuf && bulletBuf.length) blocks.push({ kind: "ul", items: bulletBuf });
    bulletBuf = null;
  };

  for (const raw of lines) {
    const line = raw.replace(/\s+$/g, "");
    if (line.trim() === "") { flushBullets(); continue; }
    // Horizontal rule (5+ dashes)
    if (/^-{5,}$/.test(line.trim())) { flushBullets(); blocks.push({ kind: "hr" }); continue; }
    // H1  (# text)
    if (/^#\s+/.test(line)) { flushBullets(); blocks.push({ kind: "h1", text: line.replace(/^#\s+/, "").trim() }); continue; }
    // H2  (## text)
    if (/^##\s+/.test(line)) { flushBullets(); blocks.push({ kind: "h2", text: line.replace(/^##\s+/, "").trim() }); continue; }
    // Numbered section heading (e.g. "1. Eligibility" or "12. Suspension and Termination")
    if (/^\d+\.\s+\S/.test(line.trim()) && line.trim().length < 120 && !line.trim().endsWith(".")) {
      flushBullets(); blocks.push({ kind: "h3", text: line.trim() }); continue;
    }
    // Bullet lines
    if (/^[•\-]\s+/.test(line.trim())) {
      if (!bulletBuf) bulletBuf = [];
      bulletBuf.push(line.trim().replace(/^[•\-]\s+/, ""));
      continue;
    }
    // Otherwise, a paragraph line
    flushBullets();
    blocks.push({ kind: "p", text: line.trim() });
  }
  flushBullets();
  return blocks;
}

function renderBlock(b, i) {
  switch (b.kind) {
    case "h1": return <h1 key={i} className="font-display text-3xl sm:text-4xl mt-2 mb-6">{b.text}</h1>;
    case "h2": return <h2 key={i} className="font-display text-2xl mt-10 mb-3">{b.text}</h2>;
    case "h3": return <h3 key={i} className="font-display text-xl mt-8 mb-3">{b.text}</h3>;
    case "hr": return <hr key={i} className="my-6 border-border" />;
    case "ul": return (
      <ul key={i} className="list-disc pl-6 space-y-1.5 mb-4 text-[15px] leading-relaxed">
        {b.items.map((it, j) => <li key={j}>{it}</li>)}
      </ul>
    );
    case "p":  return <p  key={i} className="mb-4 text-[15px] leading-relaxed">{b.text}</p>;
    default:   return null;
  }
}

/* -------------------------------------------------------------------------- */
/*  Reusable primitives for the hand-authored regulator disclosures            */
/* -------------------------------------------------------------------------- */
const H2 = ({ children }) => <h2 className="font-display text-2xl mt-2 mb-4">{children}</h2>;
const H3 = ({ children }) => <h3 className="font-display text-xl mt-8 mb-3">{children}</h3>;
const P  = ({ children }) => <p  className="mb-4 text-[15px] leading-relaxed">{children}</p>;
const L  = ({ children }) => <ul className="list-disc pl-6 space-y-1.5 mb-4 text-[15px] leading-relaxed">{children}</ul>;

/* -------------------------------------------------------------------------- */
/*  Hand-authored: State Licensing / TILA / ECOA / SCRA                        */
/*  (verbatim from the prior Legal.jsx per the user's explicit instruction)    */
/* -------------------------------------------------------------------------- */
function StateLicensing() {
  return (
    <div data-testid="legal-licensing">
      <H2>State Licensing Disclosure</H2>
      <P>FinanceLocal is a technology platform that facilitates peer-to-peer transactions in vehicle-backed notes. FinanceLocal does not itself originate consumer loans, broker consumer loans, or hold consumer funds outside of a regulated escrow arrangement.</P>
      <P>Dealers using the platform are responsible for holding all licenses required by their state of operation, including but not limited to:</P>
      <L>
        <li>State Motor Vehicle Dealer License (e.g., Texas GDN, California DMV Dealer License).</li>
        <li>State Motor Vehicle Sales Finance License where required (e.g., Texas Finance Code Chapter 348).</li>
        <li>State usury and consumer-credit compliance.</li>
      </L>
      <P>FinanceLocal verifies a dealer&apos;s submitted credentials during admin review but does not warrant that any dealer is currently in good standing with their state regulator. Users should verify a dealer&apos;s status independently before transacting.</P>
    </div>
  );
}

function TILA() {
  return (
    <div data-testid="legal-tila">
      <H2>Truth in Lending Act (TILA) Disclosure</H2>
      <P>Where a transaction on FinanceLocal constitutes a consumer credit transaction subject to the federal Truth in Lending Act (15 U.S.C. §1601 et seq.) and Regulation Z (12 C.F.R. Part 1026), the originating dealer or financing party is responsible for providing the borrower with the required TILA disclosures, including:</P>
      <L>
        <li>Amount Financed</li>
        <li>Finance Charge</li>
        <li>Annual Percentage Rate (APR)</li>
        <li>Total of Payments</li>
        <li>Payment Schedule (number, amount, and timing)</li>
        <li>Security Interest</li>
        <li>Late-payment policy and prepayment policy</li>
      </L>
      <P>FinanceLocal&apos;s platform records the principal, APR, term, payment frequency, and computed payment amount for each origination. Originators are required to incorporate these data into the TILA disclosure form they deliver to the borrower at signing.</P>
      <P>FinanceLocal is not itself a creditor and does not deliver TILA disclosures to borrowers.</P>
    </div>
  );
}

function ECOA() {
  return (
    <div data-testid="legal-ecoa">
      <H2>Equal Credit Opportunity Act (ECOA) Disclosure</H2>
      <P>The Equal Credit Opportunity Act (15 U.S.C. §1691 et seq.) and Regulation B (12 C.F.R. Part 1002) prohibit creditors from discriminating against credit applicants on the basis of race, color, religion, national origin, sex, marital status, age (provided the applicant has the capacity to contract), receipt of public assistance, or exercise of any right under the Consumer Credit Protection Act.</P>
      <H3>Manual Credit Review</H3>
      <P>Underwriting on FinanceLocal is performed <b>manually</b> by platform administrators. FinanceLocal does <b>not</b> currently perform an automated credit-bureau pull. Manual review considers only information you submit on your application, supporting documents you upload, and information lawfully obtained from public records. We do not use prohibited bases of discrimination in our review.</P>
      <H3>Adverse Action Notice</H3>
      <P>If your application is declined, withdrawn after review, or counter-offered on less-favorable terms than requested, you have the right to a statement of specific reasons for the action and may receive that statement by writing to admin@myfinancelocal.com within 60 days of the action.</P>
      <H3>Equal Credit Opportunity Act Notice</H3>
      <P>The federal agency that administers compliance with the ECOA for this platform is: Consumer Financial Protection Bureau, 1700 G Street NW, Washington, DC 20552.</P>
    </div>
  );
}

function SCRA() {
  return (
    <div data-testid="legal-scra">
      <H2>Servicemembers Civil Relief Act (SCRA) Disclosure</H2>
      <P>Active-duty servicemembers and their dependents have important protections under the Servicemembers Civil Relief Act (50 U.S.C. §§3901–4043), including:</P>
      <L>
        <li>A maximum interest rate of 6% on debts incurred before active service (with retroactive interest forgiveness above 6%).</li>
        <li>Protections against default judgments, evictions, and vehicle repossessions without a court order.</li>
        <li>The right to terminate residential leases and certain automobile leases entered into before active duty.</li>
      </L>
      <P>If you are a covered servicemember entering into a transaction on FinanceLocal, you must provide a copy of your military orders or a Defense Manpower Data Center verification to the originating dealer or note-holder to invoke SCRA protections. FinanceLocal supports SCRA-compliant servicing terms; the originating party is responsible for honoring those protections under applicable law.</P>
      <P>Questions: admin@myfinancelocal.com.</P>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Runtime source fetch                                                       */
/* -------------------------------------------------------------------------- */
function useMarkdownSource(source) {
  const [state, setState] = useState({ loading: !!source, text: null, error: null });
  useEffect(() => {
    if (!source) { setState({ loading: false, text: null, error: null }); return; }
    let cancelled = false;
    setState({ loading: true, text: null, error: null });
    fetch(source, { cache: "no-cache" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((t) => { if (!cancelled) setState({ loading: false, text: t, error: null }); })
      .catch((e) => { if (!cancelled) setState({ loading: false, text: null, error: String(e.message || e) }); });
    return () => { cancelled = true; };
  }, [source]);
  return state;
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                       */
/* -------------------------------------------------------------------------- */
export default function Legal() {
  const [params, setParams] = useSearchParams();
  const requested = (params.get("t") || "terms").toLowerCase();
  const active = SECTIONS.find((s) => s.id === requested) ? requested : "terms";
  const activeSection = SECTIONS.find((s) => s.id === active);

  const seo = SEO_BY_TAB[active] || SEO_BY_TAB.terms;
  useSEO({
    title: seo.title,
    description: seo.desc,
    canonical: `https://myfinancelocal.com/legal?t=${active}`,
    jsonLd: jsonLdBreadcrumb([
      { name: "Home",  url: "https://myfinancelocal.com/" },
      { name: "Legal Center", url: "https://myfinancelocal.com/legal" },
      { name: activeSection?.label || "Legal", url: `https://myfinancelocal.com/legal?t=${active}` },
    ]),
  });

  const setTab = (id) => setParams({ t: id }, { replace: true });

  return (
    <div data-testid="page-legal" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
      <div className="border-b border-border pb-6 mb-8">
        <div className="font-overline text-accent">Legal Center</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">FinanceLocal Legal Documents</h1>
        <p className="text-muted-foreground mt-2 text-sm max-w-3xl">
          The full text of the agreements, policies, and regulator disclosures that govern the FinanceLocal marketplace. Effective dates and version numbers are shown on each document.
        </p>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-8">
        {/* SIDEBAR — desktop */}
        <nav
          data-testid="legal-tabs-desktop"
          aria-label="Legal documents"
          className="hidden lg:block border-r border-border pr-6 self-start sticky top-24"
        >
          <ul className="space-y-0.5">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <button
                  data-testid={`legal-tab-${s.id}`}
                  onClick={() => setTab(s.id)}
                  className={
                    "w-full text-left px-3 py-2 text-sm border-l-2 transition-colors " +
                    (active === s.id
                      ? "border-primary bg-muted/50 text-primary font-semibold"
                      : "border-transparent hover:border-border hover:bg-muted/30 text-muted-foreground hover:text-foreground")
                  }
                >
                  {s.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* SIDEBAR — mobile (native select) */}
        <div className="lg:hidden" data-testid="legal-tabs-mobile">
          <label htmlFor="legal-tab-select" className="font-overline text-muted-foreground text-xs block mb-1">Document</label>
          <select
            id="legal-tab-select"
            data-testid="legal-tab-select"
            value={active}
            onChange={(e) => setTab(e.target.value)}
            className="w-full border border-border rounded-none bg-background px-3 py-2 text-sm font-mono-data"
          >
            {SECTIONS.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </div>

        {/* CONTENT */}
        <main data-testid="legal-content" className="min-w-0">
          <div className="text-xs font-overline text-muted-foreground mb-1" data-testid="legal-active-crumb">
            Legal Center · {activeSection?.label}
          </div>
          <LegalTabContent section={activeSection} />
        </main>
      </div>
    </div>
  );
}

function LegalTabContent({ section }) {
  const { loading, text, error } = useMarkdownSource(section?.source);
  if (!section) return null;
  // Hand-authored disclosures
  if (section.Component) return <section.Component />;
  // Markdown-driven documents
  if (loading) return <div data-testid="legal-loading" className="text-muted-foreground py-8">Loading document…</div>;
  if (error) return (
    <div data-testid="legal-error" className="border border-red-300 dark:border-red-900 bg-red-50 dark:bg-red-950/30 p-4 text-sm text-red-800 dark:text-red-200 flex items-start gap-2">
      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
      <div>
        <div className="font-semibold">Could not load the document.</div>
        <div className="text-xs mt-1 font-mono-data">{section.source} — {error}</div>
      </div>
    </div>
  );
  return <LegalMarkdown text={text} />;
}
